import React from "react";
import { usePublicLang } from "../../../config/i18n/public";

export default function DashboardShowcaseSection() {
  const { copy, lang } = usePublicLang();
  const c = copy.dashboard;
  const images = c.assets.macbookImages;

  const meta =
    lang === "en"
      ? [
          {
            badge: "Central Planning",
            points: [
              "Manage events, meal plans, and appointments in one place.",
              "Keep resident feedback and participation in view.",
            ],
          },
          {
            badge: "Smart Reminders",
            points: [
              "Send reminders to individuals or entire groups.",
              "With Steffi, you can reach residents directly by phone call or announcement.",
            ],
          },
          {
            badge: "Personal Profiles",
            points: [
              "Bring biography, habits, and interests together in one profile.",
              "Tailor content and conversations to each resident.",
            ],
          },
        ]
      : [
          {
            badge: "Zentrale Planung",
            points: [
              "Verwalte Veranstaltungen, Speisepläne und Termine an einem Ort.",
              "Behalte Rückmeldungen und Teilnahme der Bewohner im Blick.",
            ],
          },
          {
            badge: "Smarte Erinnerungen",
            points: [
              "Sende Erinnerungen an Einzelpersonen oder ganze Gruppen.",
              "Über Steffi erreichst du Bewohner direkt, per Anruf oder Durchsage.",
            ],
          },
          {
            badge: "Persönliche Profile",
            points: [
              "Bündele Biografie, Gewohnheiten und Interessen in einem Profil.",
              "Passe Inhalte und Gespräche individuell an Bewohner an.",
            ],
          },
        ];

  const showcases = c.items.map((item, index) => ({
    ...item,
    image: images[index],
    ...meta[index],
  }));
  const titleLines =
    lang === "en"
      ? [
          "Optimize routes and",
          "create new possibilities in",
          "social care.",
        ]
      : [
          "Optimiere Laufwege und",
          "schaffe neue Möglichkeiten in",
          "der sozialen Betreuung.",
        ];

  return (
    <section className="relative pb-24 pt-12 text-[#18306f] sm:pb-32 sm:pt-16">
      <div className="relative mx-auto w-full max-w-[1480px] px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-[1700px] text-center">
          <div className="mx-auto mb-6 h-[6px] w-24 rounded-full bg-[#77A6FF] shadow-[0_0_18px_rgba(119,166,255,0.35)]" />
          <h2 className="text-3xl font-extrabold leading-[0.94] tracking-[-0.04em] text-[#1c2f6f] sm:text-[3.75rem] lg:text-[5rem]">
            {titleLines.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </h2>
        </div>

        <div className="mt-20 space-y-24 sm:mt-24 sm:space-y-32">
          {showcases.map((item, index) => {
            const mediaLeft = index % 2 === 0;

            return (
              <article
                key={item.title}
                className={`grid items-center gap-10 lg:gap-16 ${
                  mediaLeft
                    ? "lg:grid-cols-[minmax(0,1.15fr)_minmax(340px,0.85fr)]"
                    : "lg:grid-cols-[minmax(340px,0.85fr)_minmax(0,1.15fr)]"
                }`}
              >
                <div className={mediaLeft ? "order-2 lg:order-1" : "order-2 lg:order-2"}>
                  <ShowcaseMedia
                    src={item.image}
                    alt={`${c.image_alt_prefix} ${index + 1}`}
                    align={mediaLeft ? "left" : "right"}
                  />
                </div>

                <div className={mediaLeft ? "order-1 lg:order-2" : "order-1 lg:order-1"}>
                  <div className={`mx-auto max-w-[540px] ${mediaLeft ? "lg:pl-6" : "lg:pr-6"}`}>
                    <span className="inline-flex items-center rounded-md bg-[#dfeafc] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#5b76ba]">
                      {item.badge}
                    </span>
                    <h3 className="mt-5 max-w-[16ch] text-[1.75rem] font-extrabold leading-[0.98] text-[#1a2f71] sm:text-[2.4rem] lg:text-[3rem]">
                      {item.title}
                    </h3>
                    <p className="mt-6 max-w-xl text-base leading-8 text-[#5c6e92] sm:text-lg">
                      {item.description}
                    </p>

                    <ul className="mt-8 space-y-3">
                      {item.points.map((point) => (
                        <li key={point} className="flex items-start gap-3 text-sm font-semibold text-[#445a88] sm:text-base">
                          <span className="mt-0.5 inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-[#dfe8ff] text-[#6280c6]">
                            <CheckIcon />
                          </span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ShowcaseMedia({ src, alt, align }) {
  const stageShift = align === "left" ? "lg:-ml-6" : "lg:-mr-6";

  return (
    <div className={`relative mx-auto w-full max-w-[760px] ${stageShift}`}>
      <div className="absolute inset-x-[18%] bottom-7 h-14 rounded-full bg-[#c9d7ef]/50 blur-3xl" />

      <img
        src={src}
        alt={alt}
        className="relative z-10 w-full h-auto select-none drop-shadow-[0_28px_48px_rgba(26,47,92,0.18)]"
        draggable="false"
      />
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M5 10.2 8.1 13l6.9-6.8"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
